
import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { 
  Constituency, 
  FormValues, 
  constituencyFormSchema,
  getConstituencyPartFromCode 
} from './types';
import { Country } from '@/contexts/country/types';

interface ConstituencyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingConstituency: Constituency | null;
  availableCountries: Country[];
  onSubmit: (values: FormValues) => Promise<void>;
}

const ConstituencyFormDialog = ({
  open,
  onOpenChange,
  editingConstituency,
  availableCountries,
  onSubmit,
}: ConstituencyFormDialogProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(constituencyFormSchema),
    defaultValues: {
      name: '',
      code: '',
      country_code: '',
      active: true,
    },
  });

  const watchedCountryCode = watch('country_code');

  useEffect(() => {
    if (editingConstituency) {
      reset({
        name: editingConstituency.name,
        code: getConstituencyPartFromCode(editingConstituency.code), // Extract just the constituency part
        country_code: editingConstituency.country_code,
        active: editingConstituency.active,
      });
    } else {
      reset({
        name: '',
        code: '',
        country_code: '',
        active: true,
      });
    }
  }, [editingConstituency, reset]);

  const handleFormSubmit = async (values: FormValues) => {
    await onSubmit(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {editingConstituency ? 'Edit Constituency' : 'Add New Constituency'}
          </DialogTitle>
          <DialogDescription>
            {editingConstituency
              ? 'Update the constituency information.'
              : 'Create a new constituency for the selected country.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="country_code">Country</Label>
            <Select
              value={watchedCountryCode}
              onValueChange={(value) => setValue('country_code', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent>
                {availableCountries
                  .filter((country) => country.active)
                  .map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {errors.country_code && (
              <p className="text-sm text-red-500">{errors.country_code.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="e.g., Bodden Town West"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="code">Constituency Code</Label>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded border">
                {watchedCountryCode || 'XX'}-
              </span>
              <Input
                id="code"
                placeholder="e.g., BTW"
                {...register('code')}
                className="flex-1"
              />
            </div>
            <p className="text-xs text-gray-500">
              Enter only the constituency part. The country prefix will be added automatically.
            </p>
            {errors.code && (
              <p className="text-sm text-red-500">{errors.code.message}</p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="active"
              checked={watch('active')}
              onCheckedChange={(checked) => setValue('active', checked)}
            />
            <Label htmlFor="active">Active</Label>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {editingConstituency ? 'Update' : 'Create'} Constituency
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ConstituencyFormDialog;
